import makePlainAction from '../makePlainAction';
import makeMetaKey from '../../../../utils/makeMetaKey';
import toSnakeCase from '../../../../utils/toSnakeCase';
import constructQueryString from '../../../../utils/constructQueryString';

/**
 * Creates an action that dispatchs an HTTP request. Also dispatches actions that
 * help track the lifecycle of the request. Eg. for the action `getFoo`, there
 * are 3 lifecycle events:
 *
 * * `GET_FOO_START`
 * * `GET_FOO_SUCCESS`
 * * `GET_FOO_FAILURE`
 *
 * The `_START` event should always be dispatched, while the `_SUCCESS` and
 * `_FAILURE` actions will be dispatched depending on the status code of the
 * response. The created action accepts any parameters that you can place in an
 * axios request config: https://github.com/axios/axios#request-config. Note
 * that the `url` and `method` params are set for you can cannot be overriden.
 * Change them using the appropriate parameters to your `makeHttpAction` call.
 *
 * @param {Object} options The options that configure the http actions
 * @param {string} options.model The name of the model these actions operate on
 * @param {string} options.pluralModel The plural name of the model. Defaults
 *   to the model with an 's'.
 * @param {string} options.verb The verb to be used in the action type
 * @param {string} options.method The HTTP method (eg. get, post, etc) to use
 * @param {string} options.baseUrl The root url for the API
 * @param {string} options.endpoint A custom endpoint to use. The standard
 *  endpoint is `toSnakeCase(options.model)`
 * @param {boolean} options.batch Indicates if the request is operating on multiple
 *  items (eg. fetching multiple items from an API)
 * @returns {Object} A set of 4 redux action creators.
 *
 * @public
 * @author Connor Taylor
 * @example
 *
 * // At minimum, you must provide these 4 parameters
 * let baseArgs = {
 *   model: `foo`,
 *   verb: `create`,
 *   method: `post`,
 *   baseUrl: `http://api.foo.com`,
 * };
 *
 * // Will POST the provided data to `http://api.foo.com/foo/`
 * let createFoo = makeHttpAction(baseArgs)
 * dispatch(createFoo({ data: { foo: `bar`, bar: `baz` }}));
 *
 * // To customize the endpoint it posts to, use the `endpoint` param. This
 * // will POST the provided data to `http://api.foo.com/custom/`
 * createFoo = makeHttpAction({ ...baseArgs, endpoint: `custom` });
 * dispatch(createFoo({ data: { foo: `bar`, bar: `baz` }}));
 *
 * baseArgs = {
 *   model: `foo`,
 *   verb: `get`,
 *   method: `get`,
 *   baseUrl: `http://api.foo.com`,
 * }
 *
 * // For endpoints that are specific to an item, pass the `id` param to the
 * // action. This action will GET the requested item from `http://api.foo.com/foo/1/`
 * const getFoo = makeHttpAction(baseArgs);
 * dispatch(getFoo({ id: 1 }));
 *
 * // For endpoints that return multiple items, set the `batch` param to true
 * // This action will GET the requested items at `http://api.foo.com/foo/`
 * const getFoos = makeHttpAction({ ...baseArgs, batch: true });
 * dispatch(getFoos());
 */
const makeHttpAction = ({
  model,
  pluralModel = null,
  verb,
  method,
  baseUrl,
  endpoint = ``,
  batch = false,
}) => {
  // We use metaModel here to ensure that the we get the right action types for
  // batch requests
  const pluralVersion = pluralModel || `${model}s`;
  const metaModel = `${batch ? pluralVersion : model}`;
  const onStart = makePlainAction({ verb, model: metaModel, suffix: `start` });
  const onSuccess = makePlainAction({
    verb,
    model: metaModel,
    suffix: `success`,
  });
  const onFailure = makePlainAction({
    verb,
    model: metaModel,
    suffix: `failure`,
  });

  return (args = {}) => async (dispatch, getState) => {
    const { auth } = getState();
    const qs = args.params ? constructQueryString(args.params) : ``;
    const metaKey = method === `get` ? makeMetaKey(metaModel, args.params) : ``;
    const metaInfo = metaKey && getState()[model].meta[metaKey];

    // We only need to check for next urls on get requests
    const next
      = (metaInfo && metaInfo.next) || null;
    const url
      = next
      || `${baseUrl}/${endpoint || toSnakeCase(model)}/${
        args.id ? `${args.id}/` : ``
      }${qs}`;

    const token = auth ? auth.token : null;
    const baseHeaders = {
      'Content-Type': `application/json`,
      Authorization: token ? `JWT ${token}` : undefined, // eslint-disable-line
    };
    const extraHeaders = args.headers || {};
    const fetchOptions = {
      ...args,
      method,
      headers: { ...baseHeaders, ...extraHeaders },
      // Alias args.data to body for compatibility
      body: JSON.stringify(args.body || args.data) || undefined, // eslint-disable-line
    };

    try {
      dispatch(onStart({ metaKey }));
      const response = await fetch(url, fetchOptions);
      const data = await response.json();

      // https://developer.mozilla.org/en-US/docs/Web/API/Response/ok
      if (!response.ok) {
        dispatch(onFailure({ metaKey, error: data }));
        return data;
      }

      // We use count as the indicator for when a response is paginated
      if (typeof data.count !== `undefined`) {
        dispatch(
          onSuccess({
            metaKey,
            ...data,
            data: data.results, // alias results to data for compatibility
          }),
        );
      } else {
        dispatch(onSuccess({ data, metaKey }));
      }
      return data;
    } catch (error) {
      dispatch(onFailure({ metaKey, error }));
      throw error;
    }
  };
};

/** @component */
export default makeHttpAction;
